import {
    Add,
    ArrowDropDown,
    ArrowDropUp,
    Edit,
    Email,
    FileDownload,
    NoteAdd,
} from '@mui/icons-material'
import {
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import {
    RosterProfileQuery,
    RosterProfileQueryVariables,
} from '__generated__/graphql'
import { useRef, useState } from 'react'
import SendToSearch from 'recruiter/pages/Research/SendToSearch'
import ConfirmDialog from 'shared/components/DocumentUpload/ConfirmDialog'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem } from 'shared/utils'
import { RosterCandidateTableViewId } from '../Search/RosterSearchProfile'
import AddInternalNotes from './AddInternalNotes'
import AddToRoster from './AddToRoster'
import RosterBlaster from './Blasts/RosterBlaster'
import EditColors from './EditColors'
import EditProcessStatus from './EditProcessStatus'
import EditSourceStatus from './EditSourceStatus'
import RemoveCandidatesBulkAction from './RemoveCandidates'
import ResendConfirmationEmail from './ResendConfirmationEmail'
import RosterExportBulkAction from './RosterExportBulkAction'

export type RosterProfileQueryRefetch = ApolloQueryRefetchFunction<
    RosterProfileQuery,
    RosterProfileQueryVariables
>

export interface RosterBulkActionsProps {
    selectedRosterCandidateIds: string[]
    selectedCandidateIds: string[]
    rosterId: string
    currentView: RosterCandidateTableViewId
    hasEditAccess?: boolean
    apiRef: ReturnType<typeof useGridApiRef>
}

export interface ModalState {
    message: string
    onConfirm: () => void
    open: boolean
}

const RosterBulkActions = ({
    selectedRosterCandidateIds,
    selectedCandidateIds,
    rosterId,
    currentView,
    hasEditAccess = false,
    apiRef,
}: RosterBulkActionsProps) => {
    const [modalState, setModalState] = useState({
        message: '',
        onConfirm: () => {},
        open: false,
    })

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [blasterOpen, setBlasterOpen] = useState(false)
    const [exportMenuOpen, setExportMenuOpen] = useState(false)
    const [colorsOpen, setColorsOpen] = useState(false)
    const [processStatusOpen, setProcessStatusOpen] = useState(false)
    const [sourceStatusOpen, setSourceStatusOpen] = useState(false)
    const [internalNotesOpen, setInternalNotesOpen] = useState(false)
    const [addToRosterOpen, setAddToRosterOpen] = useState(false)
    const moreActionsRef = useRef<HTMLButtonElement>(null)
    const addToRosterRef = useRef<HTMLButtonElement>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const menuId = 'more-actions-menu'
    const moreActionsButtonId = 'more-actions-button'

    const onThisRosterQuickButtons = (
        <>
            {hasEditAccess && (
                <RemoveCandidatesBulkAction
                    componentType="button"
                    selectedRosterCandidateIds={selectedRosterCandidateIds}
                    setModalState={setModalState}
                    apiRef={apiRef}
                />
            )}
            <SendToSearch
                buttonVariant="outlined"
                selectedCandidates={selectedCandidateIds}
            />
        </>
    )

    const allCandidatesButtons = (
        <>
            {hasEditAccess && (
                <Button
                    ref={addToRosterRef}
                    onClick={() => setAddToRosterOpen(true)}
                    variant="contained"
                    disabled={selectedCandidateIds.length === 0}
                >
                    <Stack direction="row" spacing={0.5} alignItems={'center'}>
                        <Add fontSize="small" />
                        <Typography variant="button">Add</Typography>
                    </Stack>
                </Button>
            )}
            <SendToSearch
                buttonVariant="outlined"
                selectedCandidates={selectedCandidateIds}
            />
        </>
    )

    return (
        <>
            <Stack
                direction="row"
                gap={getPxAsRem(8)}
                flexWrap="wrap"
                justifyContent="flex-end"
            >
                {currentView === 'all'
                    ? allCandidatesButtons
                    : onThisRosterQuickButtons}
                <Button
                    id={moreActionsButtonId}
                    variant="outlined"
                    aria-controls={open ? menuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    ref={moreActionsRef}
                >
                    <Stack direction="row" spacing={1}>
                        <Typography variant="button">More Actions </Typography>
                        {open ? <ArrowDropUp /> : <ArrowDropDown />}
                    </Stack>
                </Button>
                <Menu
                    id={menuId}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': moreActionsButtonId,
                    }}
                >
                    {hasEditAccess && currentView === 'all' && (
                        <RemoveCandidatesBulkAction
                            componentType="menuItem"
                            selectedRosterCandidateIds={
                                selectedRosterCandidateIds
                            }
                            setModalState={setModalState}
                            apiRef={apiRef}
                        />
                    )}

                    {hasEditAccess && (
                        <MenuItem
                            disabled={selectedRosterCandidateIds.length === 0}
                            onClick={() => {
                                handleClose()
                                setBlasterOpen(true)
                            }}
                        >
                            <ListItemIcon>
                                <Email />
                            </ListItemIcon>
                            <ListItemText>Blast </ListItemText>
                        </MenuItem>
                    )}

                    {hasEditAccess && (
                        <MenuItem
                            disabled={selectedRosterCandidateIds.length === 0}
                            onClick={() => {
                                handleClose()
                                setInternalNotesOpen(true)
                            }}
                        >
                            <ListItemIcon>
                                <NoteAdd />
                            </ListItemIcon>
                            <ListItemText>Add Internal Notes</ListItemText>
                        </MenuItem>
                    )}

                    {currentView === 'on-roster' && (
                        <MenuItem
                            onClick={() => {
                                handleClose()
                                setExportMenuOpen(true)
                            }}
                            disabled={selectedRosterCandidateIds.length === 0}
                        >
                            <ListItemIcon>
                                <FileDownload />
                            </ListItemIcon>
                            <ListItemText>Download</ListItemText>
                        </MenuItem>
                    )}

                    {hasEditAccess && (
                        <ResendConfirmationEmail
                            onClose={handleClose}
                            rosterId={rosterId}
                            candidateIds={selectedCandidateIds}
                            setModalState={setModalState}
                        />
                    )}

                    {hasEditAccess && (
                        <MenuItem
                            onClick={() => {
                                setColorsOpen(true)
                                handleClose()
                            }}
                            disabled={selectedRosterCandidateIds.length === 0}
                        >
                            <ListItemIcon>
                                <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Colors </ListItemText>
                        </MenuItem>
                    )}
                    {hasEditAccess && (
                        <MenuItem
                            onClick={() => {
                                setSourceStatusOpen(true)
                                handleClose()
                            }}
                            disabled={selectedRosterCandidateIds.length === 0}
                        >
                            <ListItemIcon>
                                <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Source Status </ListItemText>
                        </MenuItem>
                    )}
                    {hasEditAccess && (
                        <MenuItem
                            onClick={() => {
                                setProcessStatusOpen(true)
                                handleClose()
                            }}
                            disabled={selectedRosterCandidateIds.length === 0}
                        >
                            <ListItemIcon>
                                <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Process Status </ListItemText>
                        </MenuItem>
                    )}
                </Menu>

                {blasterOpen && (
                    <RosterBlaster
                        onClose={() => setBlasterOpen(false)}
                        isOpen={blasterOpen}
                        candidateIds={selectedCandidateIds}
                        rosterId={rosterId}
                    />
                )}

                {internalNotesOpen && (
                    <AddInternalNotes
                        onClose={() => setInternalNotesOpen(false)}
                        isOpen={internalNotesOpen}
                        rosterId={rosterId}
                        candidateIds={selectedCandidateIds}
                    />
                )}

                {exportMenuOpen && (
                    <RosterExportBulkAction
                        anchorRef={moreActionsRef}
                        onClose={() => setExportMenuOpen(false)}
                        isOpen={exportMenuOpen}
                        rosterId={rosterId}
                        candidateIds={selectedCandidateIds}
                    />
                )}

                {colorsOpen && (
                    <EditColors
                        onClose={() => setColorsOpen(false)}
                        selectedRosterCandidateIds={selectedRosterCandidateIds}
                        isOpen={colorsOpen}
                        anchorRef={moreActionsRef}
                    />
                )}

                {processStatusOpen && (
                    <EditProcessStatus
                        rosterId={rosterId}
                        onClose={() => setProcessStatusOpen(false)}
                        selectedCandidateIds={selectedCandidateIds}
                        selectedRosterCandidateIds={selectedRosterCandidateIds}
                        isOpen={processStatusOpen}
                        anchorRef={moreActionsRef}
                    />
                )}

                {sourceStatusOpen && (
                    <EditSourceStatus
                        rosterId={rosterId}
                        candidateIds={selectedCandidateIds}
                        isOpen={sourceStatusOpen}
                        anchorRef={moreActionsRef}
                        onClose={() => setSourceStatusOpen(false)}
                    />
                )}

                {addToRosterOpen && (
                    <AddToRoster
                        rosterId={rosterId}
                        selectedCandidateIds={selectedCandidateIds}
                        onClose={() => setAddToRosterOpen(false)}
                        isOpen={addToRosterOpen}
                        anchorRef={addToRosterRef}
                    />
                )}
            </Stack>

            <ConfirmDialog
                open={modalState.open}
                handleClose={() => {
                    setModalState({
                        open: false,
                        message: '',
                        onConfirm: () => {},
                    })
                }}
                handleConfirm={modalState.onConfirm}
                message={modalState.message}
            />
        </>
    )
}

export default RosterBulkActions
